import { Calculator } from "lucide-react";
import { VERSION_LABEL } from "@/lib/version";

/** Every figure the portal works out, written for the owner rather than a developer.
 *  Formula first, then what it means, then a worked example with real numbers. */
const GROUPS = [
  {
    id: "lines",
    title: "1. Invoice line items",
    rules: [
      {
        name: "Net weight (NWT)",
        formula: "NWT = GWT − ST. WT",
        plain:
          "Gross weight less whatever is not gold — stones, thread, beads. The net weight is what the customer is charged for.",
        example: "12.400 gm gross − 1.150 gm stones = 11.250 gm net",
      },
      {
        name: "Line amount",
        formula: "Amount = (NWT × rate) + (M/C % × NWT × rate) + S/C + S/P",
        plain:
          "Gold value first, then the making charge as a percentage of that gold value only — never of the stones or the set price. Stone cost and set price are added on top as flat amounts.",
        example:
          "11.250 gm × $148.00 = $1,665.00 gold · M/C 12% = $199.80 · stones $85.00 → $1,949.80",
      },
      {
        name: "Making charge or set price — one or the other",
        formula: "M/C % and S/P are never used on the same line",
        plain:
          "Filling in one greys out the other, on screen and on the server. A fixed-price piece is entered as a set price with the weight left at zero.",
        example: "Set price $450.00, weight 0.000 gm → line amount $450.00",
      },
      {
        name: "Quantity",
        formula: "Amount = qty × rate, when no weight is entered",
        plain:
          "Used for anything sold by the piece rather than by the gram, such as bullion coins or chains priced each.",
        example: "3 × $95.00 = $285.00",
      },
    ],
  },
  {
    id: "totals",
    title: "2. Invoice totals and GST",
    rules: [
      {
        name: "Subtotal",
        formula: "Subtotal = sum of every line amount",
        plain: "The prices typed on the lines are before GST. Blank lines are ignored.",
        example: "$1,949.80 + $285.00 = $2,234.80",
      },
      {
        name: "GST",
        formula: "GST = Subtotal × 10%",
        plain:
          "Added on top, never taken out of the price. Fixed at 10% for staff — only a manager or the owner can change that box, and the rule is enforced on the server too.",
        example: "$2,234.80 × 10% = $223.48",
      },
      {
        name: "Total",
        formula: "Total = Subtotal + GST",
        plain:
          "This is the whole amount the shop is owed. Nothing else is ever added to it — no fees, no charges.",
        example: "$2,234.80 + $223.48 = $2,458.28",
      },
      {
        name: "Bullion and buying contracts carry no GST",
        formula: "GST = 0 on a bullion invoice and on a buying contract",
        plain:
          "No GST is correct for investment-grade metal, 99.5% or finer. Anything like 916 or 22kt belongs on a tax invoice. On a buying contract the shop is paying the customer, so the total is what is payable to the seller.",
        example: "Bullion: Subtotal $5,000.00 → Total $5,000.00",
      },
    ],
  },
  {
    id: "fee",
    title: "3. Platform handling fee — printed, never charged",
    rules: [
      {
        name: "What it is",
        formula: "Fee = rate % of the amount taken + the fixed amount per transaction",
        plain:
          "The card or Kuber provider adds and keeps this at their own end. The shop never receives it, so it is not part of the total and not part of the balance. It is worked out and printed purely as the disclosure the customer is entitled to.",
        example: "Afterpay on $1,000.00 at 7.1% + $0.75 = $71.75 disclosed",
      },
      {
        name: "What the customer's bank shows",
        formula: "Charged at the terminal = Total (the fee is added by the provider)",
        plain:
          "Staff punch in the invoice total only. The provider adds its fee on their side, which is why the customer's statement can read more than the invoice. Adding the fee at the counter would charge the customer twice.",
        example: "Invoice $1,100.00 → punch in $1,100.00, never $1,171.75",
      },
      {
        name: "Where it appears",
        formula: "Printed on the document · included in Staff Takings · never in the total",
        plain:
          "A fee can be overridden per docket for an unusual arrangement, and an override to zero is recorded as a waiver on Staff Takings. Changing it never moves a cent of what is collected.",
        example: "Fee $0.00 override → total unchanged at $1,100.00",
      },
    ],
  },
  {
    id: "oldgold",
    title: "4. Old gold taken in part exchange",
    rules: [
      {
        name: "Trade-in value",
        formula: "Value = weight × rate − (wear and tear % of that)",
        plain:
          "The deduction covers wear, solder and loss on melt. It is explained to the customer and ticked off on the docket.",
        example: "8.500 gm × $130.00 = $1,105.00 less 5% = $1,049.75",
      },
      {
        name: "How it is applied",
        formula: "Balance = Total − payments − deposits − trade-in value",
        plain:
          "A trade-in is treated as money received, so it comes off what is still owing just like cash would.",
        example: "$2,458.28 total − $1,049.75 trade-in = $1,408.53 still to pay",
      },
    ],
  },
  {
    id: "payments",
    title: "5. Deposits, split payments and the balance",
    rules: [
      {
        name: "Paid and balance",
        formula: "Paid = deposits + payments + trade-ins · Balance = Total − Paid",
        plain:
          "Every amount here is money the shop actually received. A balance of zero stamps the document PAID IN FULL; a negative balance is credit held for the customer.",
        example: "$2,458.28 − ($500.00 deposit + $1,958.28 card) = $0.00",
      },
      {
        name: "Fill balance",
        formula: "Fill balance = whatever is still outstanding at that moment",
        plain:
          "If an item, a rate or a trade-in changes afterwards, the filled amount re-works itself so the balance never ends up a few cents out.",
        example: "Balance $1,408.53 → the row fills with $1,408.53",
      },
      {
        name: "Account credit",
        formula: "Credit can be spent once and once only",
        plain:
          "Pulling credit onto an invoice marks it used and removes it from the account. Voiding or amending that invoice hands it straight back.",
        example: "$300.00 credit → used on #213471 → account now $0.00",
      },
    ],
  },
  {
    id: "loans",
    title: "6. Pledge loan interest",
    rules: [
      {
        name: "Interest per month",
        formula: "Month's interest = principal outstanding at the start of that month × rate %",
        plain:
          "Charged on what is still owed, so money off the principal reduces the interest from the following month onwards.",
        example: "$1,000.00 at 7% = $70.00 · after $500.00 off the principal = $35.00",
      },
      {
        name: "How the months are counted",
        formula: "Whole calendar months from the start date · part of a month is charged in full",
        plain:
          "Never 30-day blocks. A loan taken on 31 January and redeemed on 28 February is one month, not two. One month is always the minimum, and the shop's stated minimum term is the floor.",
        example:
          "1 Jan → 1 Feb = 1 month ($70.00) · 1 Jan → 2 Feb = 2 months ($140.00)",
      },
      {
        name: "Interest stops at the end of the agreed term",
        formula: "Months charged are capped at the term printed on the agreement",
        plain:
          "A forgotten loan does not keep piling up interest for ever. Past the due date the agreement is in default and the pledge is dealt with under the printed terms.",
        example:
          "2-month loan left for a year: $140.00 interest, not $840.00",
      },
      {
        name: "Total due today",
        formula: "Total due = principal outstanding + (interest accrued − interest paid)",
        plain:
          "What the customer would hand over to walk out with their jewellery today.",
        example: "$500.00 principal + $35.00 interest owing = $535.00",
      },
      {
        name: "Paying more interest than has accrued",
        formula: "Anything paid above the interest accrued comes off the principal",
        plain:
          "A customer who pays ahead is never out of pocket — the extra is never kept as a windfall and never disappears.",
        example: "$100.00 paid against $70.00 of interest → $30.00 off the principal",
      },
      {
        name: "Due date",
        formula: "Due date = start date + maximum months, clamped to the month's last day",
        plain:
          "31 January plus one month is 28 February — 29 in a leap year — not 3 March. Staff can type their own due date and the interest term follows it.",
        example: "Start 31 Jan 2026, 1 month → due 28 Feb 2026",
      },
      {
        name: "A loan is not a sale",
        formula: "No GST · no handling fee · no balance on the docket",
        plain:
          "Money lent out is not takings, so it is kept out of the month's sales and GST figures and reported on its own line of the accountant spreadsheet.",
        example: "$1,000.00 lent → reported as money lent, not $1,000.00 of sales",
      },
    ],
  },
  {
    id: "repairs",
    title: "7. Repairs",
    rules: [
      {
        name: "What is owed",
        formula: "Balance = final amount (or quote) − deposit − payments",
        plain:
          "The final amount replaces the quote once the job is priced. Until then the quote is what the balance is worked out from.",
        example: "$180.00 final − $50.00 deposit = $130.00 on collection",
      },
      {
        name: "Handling fee on a repair",
        formula: "Same disclosure-only rule as an invoice",
        plain:
          "Worked out on card and Afterpay money taken against the job, printed for disclosure, and never added to what the customer owes.",
        example: "$130.00 on card at 1.75% = $2.28 disclosed, balance still $130.00",
      },
    ],
  },
  {
    id: "rates",
    title: "8. Gold rates",
    rules: [
      {
        name: "Spot to per gram",
        formula: "Per gram = spot per ounce ÷ 31.1035",
        plain: "A troy ounce is 31.1035 grams. Silver works the same way.",
        example: "$5,450.00 per oz ÷ 31.1035 = $175.22 per gram of pure gold",
      },
      {
        name: "Purity",
        formula: "24K ×0.999 · 22K ×0.916 · 21K ×0.875 · 18K ×0.750 · 14K ×0.585",
        plain: "The fineness of each karat, applied to the pure-gold per-gram figure.",
        example: "$175.22 × 0.916 = $160.50 per gram of 22K",
      },
      {
        name: "Your margin",
        formula: "Shop rate = (per gram for that purity × (1 + margin %)) + margin per gram",
        plain:
          "Set per purity and per store, as a percentage, a flat amount per gram, or both. Nothing is ever priced automatically — the rate is only suggested until somebody presses Set.",
        example: "$160.50 + 8% = $173.34, plus $2.00 per gram = $175.34",
      },
    ],
  },
  {
    id: "rounding",
    title: "9. Rounding, dates and times",
    rules: [
      {
        name: "Money",
        formula: "Every figure is rounded to the cent, half to even",
        plain:
          "The screen and the server round identically, to the same cent, so a split payment can never leave one cent owing. GST of $163.845 lands on $163.84 in both places.",
        example: "$163.845 → $163.84 · $163.855 → $163.86",
      },
      {
        name: "Weight",
        formula: "Weights are held and printed to three decimals",
        plain: "Grams to a thousandth, the same as the scales at the counter.",
        example: "11.25 → 11.250 gm",
      },
      {
        name: "Dates and times",
        formula: "Everything is Melbourne time",
        plain:
          "Every date, time, report period and sign-out is Melbourne time, never the computer's own clock or UTC. That is why a sale at 10pm never lands on yesterday's takings.",
        example: "A docket saved 10:30pm 6 Oct is on 6 Oct's report",
      },
    ],
  },
  {
    id: "reporting",
    title: "10. What counts towards the reports",
    rules: [
      {
        name: "Accountant export",
        formula: "Active invoices, bullion invoices and buying contracts only",
        plain:
          "Parked dockets, reserved numbers and voided documents are left out. Pledge loans are shown separately as money lent, because lending is not a sale.",
        example: "408 documents, $427,707.84 total, loans on their own line",
      },
      {
        name: "Cash watch",
        formula: "Cash in or out of $10,000 or more is flagged",
        plain:
          "Counted per customer and per day, across invoices, contracts and loan payouts, for the AUSTRAC threshold.",
        example: "$6,000 then $4,500 cash on one day = flagged",
      },
      {
        name: "Money owed",
        formula: "Anything with a balance over nil, plus loans still to redeem",
        plain:
          "Invoices, contracts and repairs with money still owing, credit being held, and pledge loans — a loan's figure is principal plus interest worked out to today under the rules above, not a docket balance. Filter by Loans to see just those.",
        example: "#213471 $1,408.53 · 12 days · loan #372 $535.00 due 28 Feb",
      },
    ],
  },
];

export default function Calculations() {
  return (
    <div className="fade-up" data-testid="calculations-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#B38F4E]">
            Owner reference · {VERSION_LABEL}
          </p>
          <h1 className="mt-2 flex items-center gap-3 text-3xl font-bold sm:text-4xl">
            <Calculator className="h-7 w-7 text-[#C5A059]" />
            How every figure is worked out
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            Every calculation the portal performs, in one place — the formula, what it
            means in plain English, and a worked example. Nothing here is hidden
            anywhere else in the app; this is the same arithmetic the invoices, loans
            and reports use.
          </p>
        </div>
        <button
          onClick={() => window.print()}
          className="h-11 rounded-none border border-[#C5A059] px-6 text-sm font-semibold transition-colors duration-150 hover:bg-[#FBF8F2]"
          data-testid="calculations-print-button"
        >
          Print / save as PDF
        </button>
      </div>

      <div className="no-print mt-6 flex flex-wrap gap-2 border border-border p-4">
        {GROUPS.map((g) => (
          <a
            key={g.id}
            href={`#${g.id}`}
            className="border border-border px-3 py-1.5 text-xs transition-colors duration-150 hover:bg-[#FBF8F2]"
            data-testid={`calculations-jump-${g.id}`}
          >
            {g.title}
          </a>
        ))}
      </div>

      <div className="print-area mt-8 max-w-4xl space-y-10">
        {GROUPS.map((group) => (
          <section key={group.id} id={group.id} data-testid={`calculations-group-${group.id}`}>
            <h2 className="border-b-2 border-[#C5A059] pb-2 text-base font-bold uppercase tracking-wider md:text-lg">
              {group.title}
            </h2>
            <div className="mt-5 space-y-5">
              {group.rules.map((rule, i) => (
                <div
                  key={i}
                  className="border-l-2 border-[#E8DFC8] pl-4"
                  data-testid={`calculations-rule-${group.id}-${i}`}
                >
                  <h3 className="text-sm font-bold uppercase tracking-wider text-[#8A6A22]">
                    {rule.name}
                  </h3>
                  <p className="tabular mt-2 bg-[#FBF8F2] px-3 py-2 text-sm font-semibold">
                    {rule.formula}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed">{rule.plain}</p>
                  <p className="tabular mt-1.5 text-xs text-muted-foreground">
                    Example: {rule.example}
                  </p>
                </div>
              ))}
            </div>
          </section>
        ))}
        <p className="border-t border-border pt-5 text-xs text-muted-foreground">
          Dhunna Jewellers Sales Portal · {VERSION_LABEL} · all amounts in AUD, all times
          Melbourne time. If a figure on a document ever disagrees with this page, the
          page is wrong — tell us and it will be corrected.
        </p>
      </div>
    </div>
  );
}
